/**
 * CertificatePage - View and download certificate
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, Download, ArrowRight, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper for consistent timezone-aware date formatting
const formatDateTime = (isoString, options = {}) => {
  if (!isoString) return '—';
  try {
    const date = new Date(isoString);
    const defaultOptions = {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...options
    };
    return new Intl.DateTimeFormat('en-US', defaultOptions).format(date);
  } catch {
    return isoString;
  }
};

const CertificatePage = () => {
  const navigate = useNavigate();
  const { relationshipId: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resolve relationship_id: URL param → query → sessionStorage. Persist for child pages.
  const resolvedId = paramId || searchParams.get('relationshipId') || sessionStorage.getItem('relationship_id');
  const token = searchParams.get('token') || sessionStorage.getItem('reveal_token') || '';

  useEffect(() => {
    if (resolvedId) {
      sessionStorage.setItem('relationship_id', resolvedId);
    }
    if (token) {
      sessionStorage.setItem('reveal_token', token);
    }
    fetchCertificate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedId]);

  const fetchCertificate = async () => {
    try {
      const relationshipId = resolvedId;
      if (!relationshipId) {
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/certificates/${relationshipId}`);
      setCertificate(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching certificate:', error);
      toast.error('Failed to load certificate');
      setLoading(false);
    }
  };

  const handleDownload = () => {
    const relationshipId = resolvedId;
    if (!relationshipId) return;
    window.open(`${API}/certificates/${relationshipId}/download`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-stone-400 mx-auto mb-4" />
            <p className="text-stone-600">Certificate not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-mc-blue-deep" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 mb-3 sm:mb-4 break-words px-2">Your MarrsingClause® is Complete!</h2>
            <p className="text-sm sm:text-base md:text-lg text-stone-600 px-2 break-all">Certificate ID: {certificate.public_certificate_id}</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 shadow-lg p-6 sm:p-8 md:p-12 mb-6 sm:mb-8">
            <div className="text-center mb-6 sm:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900 mb-2">Certificate of Conscious Commitment</h3>
              <p className="text-sm sm:text-base text-stone-600">MarrsingClause® {certificate.version}</p>
            </div>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-stone-100 gap-1 sm:gap-0">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-stone-500">Certificate ID</span>
                <span className="font-semibold text-stone-900 text-sm sm:text-base break-all">{certificate.public_certificate_id}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-stone-100 gap-1 sm:gap-0">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-stone-500">Issued</span>
                <span className="font-semibold text-stone-900 text-sm sm:text-base">{formatDateTime(certificate.issued_at)}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 border-b border-stone-100 gap-1 sm:gap-0">
                <span className="text-xs sm:text-sm uppercase tracking-widest text-stone-500">Status</span>
                <span className="font-semibold text-mc-blue-deep text-sm sm:text-base">{certificate.status}</span>
              </div>
            </div>

            <button
              onClick={handleDownload}
              className="w-full bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
              data-testid="download-certificate"
            >
              <Download className="w-5 h-5" />
              Download PDF Certificate
            </button>
          </div>

          <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 text-white">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-amber-200" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/50">New</p>
                <h3 className="text-xl sm:text-2xl font-semibold mt-1">Your Marrsing Alignment Intelligence™</h3>
              </div>
            </div>
            <p className="text-sm sm:text-base text-white/75 leading-7">
              Explore your shared themes, growth edges, and tier-toned discussion prompts. A living conversation tool, designed to turn insight into connection.
            </p>
            <button
              onClick={() => navigate('/alignment-intelligence')}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-stone-900 hover:bg-stone-100 px-6 py-3 text-sm sm:text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
              data-testid="explore-alignment-intelligence-btn"
            >
              Explore Alignment Intelligence
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="bg-stone-50 rounded-2xl border border-stone-100 p-6 sm:p-8 text-center">
            <h3 className="text-lg sm:text-xl font-semibold text-stone-900 mb-3 sm:mb-4">Continue Your Journey</h3>
            <p className="text-sm sm:text-base text-stone-600 mb-4 sm:mb-6">
              Unlock guided renewals, dashboards, and structured check-ins in the advanced MarrsingClause® app.
            </p>
            <button
              onClick={() => navigate('/upgrade')}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-6 sm:px-8 py-3 text-sm sm:text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
            >
              Explore Advanced Features
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificatePage;
